import React from "react"
import { LoadingRegion } from "components/custom/Loader"
import { Button, Drawer, Form, Input } from "antd"
import type { FormInstance } from "antd"
import { Button as CustomButton } from "components"
import CustomPagination from "./CustomPagination"
import SetupWorkplaceList from "./SetupWorkplaceList"
import type { SetupWorkplace } from "./SetupWorkplaceList"
import {
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import WorkplaceFaqModal from "../../components/WorkplaceFaqModal"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import CustomInput from "./CustomInput"
import type { IState } from "pages/dashboard/people/people/typings"
import { useQuery } from "@tanstack/react-query"
import { COMMON_ENDPOINTS, commonService } from "services/commonService"
import GeoPicker from "components/GeoPicker"
import {
  calculateRadius,
  getAddressComponent,
  matchState,
  toSelectOptions,
} from "../../utils/shiftTablesUtils"
import { CustomBackButton } from "../Setup"
import { useAppNotificationStore } from "stores"

// ─── Domain types ─────────────────────────────────────────────────────────────

export interface OfficeOption {
  code: string
  description: string
}

export interface AddressComponent {
  types: string[]
  short_name: string
  long_name: string
}

interface PlaceDetails {
  address_components?: AddressComponent[]
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  workplaces: SetupWorkplace[]
  selectedWorkplaceId: string
  selectedWorkplaceHasCoordinates?: boolean
  onSelect: (workplaceId: string) => void
  onConfigureAddress?: (workplace: SetupWorkplace) => void
  page: number
  total: number
  onPageChange: (page: number) => void
  onCreateNewWorkplaceClick: () => void
  onEditWorkplaceClick: (selectedWorkplace: SetupWorkplace) => void
  onBack: () => void
  onNext: () => void
  isConfiguringWorkplace?: boolean
  isSavingProgress?: boolean
  isLoading?: boolean
  searchValue: string
  onSearchChange: (value: string) => void
  configureAddressWorkplace: SetupWorkplace | null
  createWorkplaceDrawerOpen: boolean
  closeCreateWorkplaceDrawer: () => void
  taxOffices: OfficeOption[]
  isTaxOfficesLoading: boolean
  housingLocations: OfficeOption[]
  isHousingLocationsLoading: boolean
  isCreatingWorkplace: boolean
  isUpdatingWorkplace: boolean
  alpha2Code: string
}

// ─── Component ────────────────────────────────────────────────────────────────

const SetupWorkplaceSelection: React.FC<Props> = ({
  workplaces,
  selectedWorkplaceId,
  selectedWorkplaceHasCoordinates = false,
  onSelect,
  onConfigureAddress,
  page,
  total,
  onPageChange,
  onCreateNewWorkplaceClick,
  onBack,
  onNext,
  isConfiguringWorkplace = false,
  isSavingProgress = false,
  isLoading = false,
  searchValue,
  onSearchChange,
  configureAddressWorkplace,

  alpha2Code,
  onEditWorkplaceClick,
}) => {
  const { data: statesRes } = useQuery<IBaseRes<IState[]>>({
    queryKey: [COMMON_ENDPOINTS.GET_STATES_BY_COUNTRY, alpha2Code],
    queryFn: () => commonService.getStatesByCountry(alpha2Code),
    enabled: !!alpha2Code,
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })
  const states = statesRes?.data

  const { toast } = useAppNotificationStore()
  const [canCreateWorkplace] = usePermissions({
    allowedPermissions: [TimeAndAttendancePermission.CanCreateWorkplaces],
  })

  const canProceed =
    !!selectedWorkplaceId &&
    selectedWorkplaceHasCoordinates &&
    !isSavingProgress

  const handleNextStep = () => {
    if (!selectedWorkplaceId) {
      toast.error("Please select a workplace")
      return
    }

    if (!selectedWorkplaceHasCoordinates) {
      toast.error(
        "The selected workplace address doesn't have coordinates on the map. Please edit the address to configure the location."
      )
      return
    }

    if (canProceed) onNext()
  }

  // const handlePlaceDetails = (place: PlaceDetails) => {
  //   try {
  //     const comps = place.address_components ?? []
  //     const countryComp = getAddressComponent(comps, "country")
  //     const stateComp = getAddressComponent(
  //       comps,
  //       "administrative_area_level_1"
  //     )

  //     const countryCode = countryComp?.short_name ?? ""
  //     const stateName = stateComp?.long_name ?? stateComp?.short_name ?? ""
  //     const stateShortName = stateComp?.short_name ?? ""
  //     const stateValue = matchState(states ?? [], stateName, stateShortName)

  //     const currentValues = createWorkplaceForm.getFieldsValue()

  //     createWorkplaceForm.setFieldsValue({
  //       country: countryCode,
  //       state: stateValue,
  //     })

  //     handleFormValuesChange(
  //       { country: countryCode },
  //       { ...currentValues, country: countryCode }
  //     )
  //     handleFormValuesChange(
  //       { state: stateValue },
  //       { ...currentValues, state: stateValue }
  //     )
  //   } catch {
  //     // Silently ignore address parse failures
  //   }
  // }

  const isEditingAddress = !!configureAddressWorkplace

  return (
    <>
      {/* ── Main content ─────────────────────────────────────────────────── */}
      {/* ── Header (static) ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 shrink-0">
        <CustomBackButton onBack={onBack} />

        <div>
          <h5 className="text-2xl text-[#0F172B] font-bold">
            Select a workplace to configure
          </h5>
          <p className="text-sm text-slate-900 font-normal">
            Time and attendance is configured per workplace. Choose an existing
            workplace or create a new one.
          </p>
        </div>

        <WorkplaceFaqModal />
      </div>

      {/* ── Scrollable content ───────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6 pt-6 pr-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 [&_.ant-input-affix-wrapper]:!h-10">
            <Input
              placeholder="Search for a workplace"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              prefix={
                <MagnifyingGlassIcon className="w-4 h-4 text-[#020618]" />
              }
              className="h-10 text-sm"
              allowClear
            />
          </div>
          <Button
            type="default"
            className="border-2 border-[#004AF5] text-[#004AF5] font-semibold h-10 flex items-center gap-2"
            onClick={() => {
              if (!canCreateWorkplace) {
                toast.unauthorized()
                return
              }
              onCreateNewWorkplaceClick()
            }}
          >
            <PlusIcon className="w-4 h-4" />
            Create new workplace
          </Button>
        </div>

        <LoadingRegion spinning={isLoading}>
          <SetupWorkplaceList
            workplaces={workplaces}
            selectedWorkplaceId={selectedWorkplaceId}
            onSelect={onSelect}
            onConfigureAddress={onConfigureAddress}
            onEditWorkplace={onEditWorkplaceClick}
          />
        </LoadingRegion>

        <div className="pt-4 flex items-center justify-between">
          <CustomPagination
            page={page}
            total={total}
            onChange={onPageChange}
          />
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div className="shrink-0 pt-4 border-t border-[#E2E8F0] flex justify-end">
        <CustomButton
          color="primary"
          className="h-10 px-6 rounded-md font-semibold"
          submitting={isConfiguringWorkplace || isSavingProgress}
          disabled={!selectedWorkplaceId}
          onClick={handleNextStep}
        >
          Next step
        </CustomButton>
      </div>

      {/* ── Create / Edit drawer ─────────────────────────────────────────── */}
    </>
  )
}

export default SetupWorkplaceSelection
